import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AGENT_FLAG,
  agentConversationDetailSchema,
  agentConversationSchema,
  agentMentionListSchema,
  mentionToken,
  messageParts,
  type AgentConversation,
  type AgentDashboardView,
  type AgentMention,
  type AgentMentionKind,
  type AgentMentionList,
} from '@forecastify/shared';
import {
  Button,
  DropdownMenu,
  Icon,
  MentionPicker,
  Sheet,
  Textarea,
  cn,
  mentionPickerOrder,
  useIsMobile,
  type IconName,
  type MenuItem,
  type MentionPickerGroup,
} from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { useSession } from '../../session';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { mentionQuery } from '../tasks/comment-composer';
import { AgentMarkdown } from './agent-markdown';
import { agentTurn, AgentRefusal } from './agent-turn';
import { useFollowThread } from './follow-thread';
import { ThinkingIndicator } from './thinking-orb';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 19 Oct 2026 · THE DASHBOARD CHAT — a bar at the bottom of a project's      │
 * │ Áttekintés and Pénzügy tabs, and the popup thread it opens. AG-1.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: ask the agent only about what is on the dashboard, get quick insight answers,
 * and `@` anything relevant — a contract, a line item, a period, a person, a task, an invoice, an
 * external cost. The threads are the Agent's own (`origin = 'dashboard'`), pinned server-side to
 * the project; this component is the surface and nothing more.
 *
 * ⚠️⚠️ **A LEAN THREAD, NOT THE AGENT PAGE'S.** No proposals (the thread is read-only), no files, no
 * export offers, no per-tool rows — a quick answer shows one quiet *"reading the figures"* line
 * while the tools run. Rebuilding the Agent page's frame reducer here would carry four features this
 * surface refuses by design; `agentTurn` and `AgentMarkdown` are what it shares.
 *
 * ⚠️ **The view travels with every message.** The tab hands it in as it stands now, so *"miért ilyen
 * magas?"* is asked about the contract, period, EAC and day on the screen at that moment.
 *
 * ⚠️ **Placement.** The bar is the last thing in the tab's scroll container, `sticky` to its
 * bottom, on every screen — a floating bar, the product owner's word. 20 Oct 2026: on a phone it was
 * pinned above the content instead, and that was the bug. On a desktop the thread pops up above it;
 * on a phone it opens as a full-screen drawer with no expand button.
 */

interface ChatTurn {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly text: string;
  /** While the reply is being written. */
  readonly pending: boolean;
  /** What the pending reply is doing, for the quiet line under it. */
  readonly stage: 'thinking' | 'reading' | 'writing';
  readonly error: MessageKey | null;
  /** 29 Oct 2026 · the thread before this reply was summarised — `agentMessageSchema.compacted`. */
  readonly compacted: boolean;
}

const KIND_ICON: Readonly<Record<AgentMentionKind, IconName>> = {
  contract: 'file-text',
  lineItem: 'list',
  period: 'calendar',
  person: 'user-round',
  task: 'circle-check',
  invoice: 'credit-card',
  externalCost: 'building',
};

const conversationListSchema = agentConversationSchema.array();

/** ⚠️ Literal keys, one per branch — a computed key is invisible to the catalogue check. */
function errorKey(code: string): MessageKey {
  if (code === 'rate_limited') return 'agent.error.rate_limited';
  if (code === 'turn_too_long') return 'agent.error.turn_too_long';
  if (code === 'upstream_unavailable') return 'agent.error.upstream_unavailable';
  if (code === 'conversation_not_found') return 'agent.error.conversation_not_found';
  return 'agent.error.unknown';
}

/**
 * The text as the server is sent it: every `@label` the person kept, turned into its token.
 *
 * ⚠️ **Longest label first**, so `@Build` cannot eat the front of `@Build 2`. A mention whose label
 * is no longer in the text was un-typed and is dropped — picking something and deleting it is an
 * ordinary edit (`comment-composer.tsx`'s `keptMentions` makes the same call).
 */
export function wireText(text: string, picked: readonly AgentMention[]): string {
  let out = text;
  for (const mention of [...picked].sort((a, b) => b.label.length - a.label.length)) {
    const typed = `@${mention.label}`;
    const at = out.indexOf(typed);
    if (at === -1) continue;
    out = `${out.slice(0, at)}${mentionToken(mention)}${out.slice(at + typed.length)}`;
  }
  return out;
}

export function DashboardChat({
  projectId,
  view,
}: {
  readonly projectId: string;
  readonly view: AgentDashboardView;
}): ReactNode {
  const mobile = useIsMobile();
  const enabled = useSidebarContext()?.view.features[AGENT_FLAG] === true;
  if (!enabled) return null;
  return <Chat projectId={projectId} view={view} mobile={mobile} />;
}

function Chat({
  projectId,
  view,
  mobile,
}: {
  readonly projectId: string;
  readonly view: AgentDashboardView;
  readonly mobile: boolean;
}): ReactNode {
  const t = useT();
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<readonly ChatTurn[]>([]);
  const [threads, setThreads] = useState<readonly AgentConversation[]>([]);
  const abort = useRef<AbortController | null>(null);
  const busy = turns.some((turn) => turn.pending);

  /* ⚠️ The latest view, read at send time — a stale closure would describe the screen as it was
     when the composer first rendered. */
  const viewRef = useRef(view);
  viewRef.current = view;

  const loadThreads = useCallback(async (): Promise<void> => {
    const response = await session
      .request(`/agent/conversations?projectId=${projectId}`)
      .catch(() => null);
    if (response?.ok !== true) return;
    const parsed = conversationListSchema.safeParse(await response.json());
    if (parsed.success) setThreads(parsed.data);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [projectId]);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  /* A turn still streaming when the tab goes away stops costing money (`agent.controller.ts`). */
  useEffect(
    () => () => {
      abort.current?.abort();
    },
    [],
  );

  const openThread = async (id: string): Promise<void> => {
    abort.current?.abort();
    const response = await session.request(`/agent/conversations/${id}`).catch(() => null);
    if (response?.ok !== true) return;
    const parsed = agentConversationDetailSchema.safeParse(await response.json());
    if (!parsed.success) return;
    setConversationId(id);
    setTurns(
      parsed.data.messages.map((message) => ({
        id: message.id,
        role: message.role,
        text: message.text,
        pending: false,
        stage: 'writing' as const,
        error: null,
        compacted: message.compacted,
      })),
    );
    setOpen(true);
  };

  const startNew = (): void => {
    abort.current?.abort();
    setConversationId(null);
    setTurns([]);
  };

  const patchLast = (patch: Partial<ChatTurn>): void => {
    setTurns((current) => {
      const last = current.at(-1);
      if (last?.role !== 'assistant') return current;
      return [...current.slice(0, -1), { ...last, ...patch }];
    });
  };

  const send = async (text: string): Promise<void> => {
    setOpen(true);
    const controller = new AbortController();
    abort.current = controller;
    const asked = Date.now();
    setTurns((current) => [
      ...current,
      {
        id: `u${String(asked)}`,
        role: 'user',
        text,
        pending: false,
        stage: 'writing',
        error: null,
        compacted: false,
      },
      {
        id: `a${String(asked)}`,
        role: 'assistant',
        text: '',
        pending: true,
        stage: 'thinking',
        error: null,
        compacted: false,
      },
    ]);

    /**
     * ⚠️ 29 Oct 2026 · the turn ends at `message_stop`, and the stream may outlive it by a frame — the
     * thread's summarised title (`0246`). Once finished, nothing below may touch the LAST reply: by
     * then it can be the next question's, still streaming. `agent-route.tsx`'s `consume` makes the
     * same guard for the same reason.
     */
    let finished = false;
    try {
      let id = conversationId;
      if (id === null) {
        const created = await session.request('/agent/conversations', {
          method: 'POST',
          body: JSON.stringify({ projectId }),
        });
        if (!created.ok) throw new AgentRefusal(`http_${String(created.status)}`, created.status);
        const parsed = agentConversationSchema.parse(await created.json());
        id = parsed.id;
        setConversationId(id);
      }

      let written = '';
      const thread = id;
      for await (const frame of agentTurn(
        session.request,
        id,
        text,
        controller.signal,
        viewRef.current,
      )) {
        /* 29 Oct 2026 · the provisional title, then its summary — straight into the history menu. */
        if (frame.type === 'conversation_title') {
          setThreads((current) =>
            current.map((entry) =>
              entry.id === thread ? { ...entry, title: frame.title } : entry,
            ),
          );
          continue;
        }
        if (finished) continue;
        if (frame.type === 'text_delta') {
          written += frame.text;
          patchLast({ text: written, stage: 'writing' });
        } else if (frame.type === 'tool_start') {
          patchLast({ stage: 'reading' });
        } else if (frame.type === 'thinking_start') {
          patchLast({ stage: 'thinking' });
        } else if (frame.type === 'compacted') {
          patchLast({ compacted: true });
        } else if (frame.type === 'error') {
          patchLast({ pending: false, error: errorKey(frame.code) });
          return;
        } else if (frame.type === 'message_stop') {
          patchLast({ pending: false, id: frame.messageId });
          finished = true;
        }
      }
      if (!finished) patchLast({ pending: false });
    } catch (error) {
      /* ⚠️ After `message_stop` the answer is whole; a lost title frame is not a failed reply. */
      if (controller.signal.aborted || finished) return;
      patchLast({
        pending: false,
        error: errorKey(error instanceof AgentRefusal ? error.code : 'unknown'),
      });
    } finally {
      void loadThreads();
    }
  };

  const history = (
    <HistoryMenu
      threads={threads}
      openId={conversationId}
      onOpen={(id) => {
        void openThread(id);
      }}
      onNew={startNew}
    />
  );

  const thread = (
    <div className="flex min-h-0 flex-1 flex-col">
      <Thread turns={turns} />
      <div className="p-3 pt-0">
        <Composer
          projectId={projectId}
          busy={busy}
          placeholder={t('dashChat.reply')}
          onSend={(text) => {
            void send(text);
          }}
          autoFocus
        />
      </div>
    </div>
  );

  if (mobile) {
    return (
      <>
        <div className="pointer-events-none sticky bottom-0 z-40 mt-auto px-3 pt-2 pb-3">
          <div className="pointer-events-auto">
            <Composer
              projectId={projectId}
              busy={busy}
              placeholder={t('dashChat.placeholder')}
              leading={history}
              floating
              onSend={(text) => {
                void send(text);
              }}
              onFocus={() => {
                if (turns.length > 0) setOpen(true);
              }}
            />
          </div>
        </div>
        <Sheet
          open={open}
          onOpenChange={setOpen}
          title={t('dashChat.title')}
          closeLabel={t('common.close')}
          headerRight={
            <div className="flex items-center gap-1">
              {history}
              <NewButton onNew={startNew} />
            </div>
          }
        >
          {thread}
        </Sheet>
      </>
    );
  }

  return (
    <div className="pointer-events-none sticky bottom-0 z-40 mt-auto flex justify-center px-4 pt-2 pb-4">
      <div className="pointer-events-auto relative w-full max-w-[640px]">
        {open ? (
          <section
            aria-label={t('dashChat.title')}
            className={cn(
              'bg-popover text-popover-foreground shadow-modal pop-in absolute right-0 bottom-0 flex flex-col overflow-hidden rounded-[14px] border',
              expanded ? 'h-[min(78vh,760px)] w-[min(92vw,920px)]' : 'h-[min(70vh,520px)] w-full',
            )}
          >
            <header className="flex items-center gap-1 border-b px-3 py-2">
              <Icon name="sparkles" size={14} className="text-primary" />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                {t('dashChat.title')}
              </span>
              {history}
              <NewButton onNew={startNew} />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={expanded ? t('dashChat.shrink') : t('dashChat.expand')}
                title={expanded ? t('dashChat.shrink') : t('dashChat.expand')}
                onClick={() => {
                  setExpanded((current) => !current);
                }}
              >
                <Icon name={expanded ? 'minimize' : 'maximize'} size={14} />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('dashChat.minimise')}
                title={t('dashChat.minimise')}
                onClick={() => {
                  setOpen(false);
                }}
              >
                <Icon name="chevron-down" size={15} />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('common.close')}
                title={t('common.close')}
                onClick={() => {
                  setOpen(false);
                  setExpanded(false);
                  startNew();
                }}
              >
                <Icon name="x" size={15} />
              </Button>
            </header>
            {thread}
          </section>
        ) : (
          <Composer
            projectId={projectId}
            busy={busy}
            placeholder={t('dashChat.placeholder')}
            leading={history}
            floating
            onSend={(text) => {
              void send(text);
            }}
            onFocus={() => {
              if (turns.length > 0) setOpen(true);
            }}
          />
        )}
      </div>
    </div>
  );
}

function NewButton({ onNew }: { readonly onNew: () => void }): ReactNode {
  const t = useT();
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={t('dashChat.new')}
      title={t('dashChat.new')}
      onClick={onNew}
    >
      <Icon name="plus" size={15} />
    </Button>
  );
}

function HistoryMenu({
  threads,
  openId,
  onOpen,
  onNew,
}: {
  readonly threads: readonly AgentConversation[];
  readonly openId: string | null;
  readonly onOpen: (id: string) => void;
  readonly onNew: () => void;
}): ReactNode {
  const t = useT();
  const items: MenuItem[] = [
    { label: t('dashChat.new'), icon: 'plus', onSelect: onNew },
    ...(threads.length === 0 ? [] : [{ type: 'separator' as const, label: '' }]),
    ...threads.map((thread) => ({
      label: thread.title ?? t('agent.untitled'),
      checked: thread.id === openId ? true : undefined,
      onSelect: () => {
        onOpen(thread.id);
      },
    })),
  ];
  return (
    <DropdownMenu
      items={items}
      width={280}
      side="top"
      align="end"
      label={t('dashChat.history')}
      searchPlaceholder={t('dashChat.historySearch')}
      emptyLabel={t('dashChat.historyEmpty')}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t('dashChat.history')}
        title={t('dashChat.history')}
      >
        <Icon name="clock" size={15} />
      </Button>
    </DropdownMenu>
  );
}

/* ── the thread ──────────────────────────────────────────────────────────────────────────────── */

function Thread({ turns }: { readonly turns: readonly ChatTurn[] }): ReactNode {
  const t = useT();
  /* ⚠️ Follow the reply as it streams, but only while the reader is at the bottom —
     `follow-thread.ts`, shared with the full Agent screen, which had the same defect. */
  const follow = useFollowThread(turns.filter((turn) => turn.role === 'user').at(-1)?.id ?? null);

  return (
    <div
      ref={follow.ref}
      onScroll={follow.onScroll}
      className="min-h-0 flex-1 overflow-y-auto px-4 py-3"
    >
      {turns.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-[13px]">{t('dashChat.empty')}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {turns.map((turn) => (
            <div
              key={turn.id}
              className={cn(
                'flex flex-col gap-1',
                turn.role === 'user' ? 'items-end' : 'items-start',
              )}
            >
              <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                {turn.role === 'user' ? t('agent.you') : t('agent.agent')}
              </span>
              {turn.role === 'user' ? (
                <div className="bg-muted max-w-[85%] rounded-lg px-3 py-2 text-[13.5px] whitespace-pre-wrap">
                  <MessageText text={turn.text} />
                </div>
              ) : (
                <div className="w-full text-[13.5px]">
                  {/* 29 Oct 2026 · the Agent page's line, in this thread's quieter size. */}
                  {turn.compacted ? (
                    <p
                      className="text-muted-foreground mb-1 flex items-center gap-1.5 text-[12px]"
                      data-agent-compacted
                    >
                      <Icon name="file-text" size={12} aria-hidden />
                      {t('agent.turn.compacted')}
                    </p>
                  ) : null}
                  {turn.text === '' ? null : <AgentMarkdown text={turn.text} />}
                  {turn.pending && turn.stage !== 'writing' ? (
                    <span className="text-muted-foreground flex items-center gap-2 text-[12.5px]">
                      <ThinkingIndicator label={t('dashChat.thinking')} />
                      {turn.stage === 'reading' ? t('dashChat.reading') : t('dashChat.thinking')}
                    </span>
                  ) : null}
                  {turn.error === null ? null : (
                    <p className="text-destructive text-[12.5px]">{t(turn.error)}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A stored question, with every mention drawn as a chip where it was typed. */
function MessageText({ text }: { readonly text: string }): ReactNode {
  return (
    <>
      {messageParts(text).map((part, index) =>
        part.type === 'text' ? (
          <span key={index}>{part.text}</span>
        ) : (
          <span
            key={index}
            className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-sm px-1 font-medium"
          >
            <Icon name={KIND_ICON[part.kind]} size={11} />
            {part.label}
          </span>
        ),
      )}
    </>
  );
}

/* ── the composer ────────────────────────────────────────────────────────────────────────────── */

function Composer({
  projectId,
  busy,
  placeholder,
  leading,
  floating = false,
  autoFocus = false,
  onSend,
  onFocus,
}: {
  readonly projectId: string;
  readonly busy: boolean;
  readonly placeholder: string;
  /** The history button, on the bar. */
  readonly leading?: ReactNode;
  /** The bar at rest — a raised pill rather than the thread's footer. */
  readonly floating?: boolean;
  readonly autoFocus?: boolean;
  readonly onSend: (text: string) => void;
  readonly onFocus?: () => void;
}): ReactNode {
  const t = useT();
  const session = useSession();
  const field = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [picked, setPicked] = useState<readonly AgentMention[]>([]);
  const [list, setList] = useState<AgentMentionList | null>(null);
  const [more, setMore] = useState<AgentMentionKind | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const query = mentionQuery(text, caret);
  const picking = query !== null && !dismissed;

  /* The picker's list, for what is typed after the `@` — debounced, and dropped when stale. */
  useEffect(() => {
    if (!picking) {
      setList(null);
      return;
    }
    /* ⚠️ Aborted when the query moves on, so a slow answer for `@Na` cannot land over `@Nagy`'s. */
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams();
      if (query !== '') params.set('q', query);
      if (more !== null) params.set('kind', more);
      void session
        .request(`/agent/dashboard/${projectId}/mentions?${params.toString()}`, {
          signal: controller.signal,
        })
        .then(async (response) => {
          if (!response.ok) return;
          const parsed = agentMentionListSchema.safeParse(await response.json());
          if (parsed.success && !controller.signal.aborted) setList(parsed.data);
        })
        .catch(() => undefined);
    }, 150);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [picking, query, more, projectId]);

  const groups: MentionPickerGroup[] = useMemo(
    () =>
      (list?.groups ?? []).map((group) => ({
        key: group.kind,
        label: kindLabel(t, group.kind),
        total: group.total,
        items: group.options.map((option) => ({
          id: option.id,
          label: option.label,
          detail: option.detail,
          avatarUrl: option.avatarUrl,
          color: option.color,
          icon: KIND_ICON[option.kind],
        })),
      })),
    [list, t],
  );
  const order = mentionPickerOrder(groups);
  const highlighted = active !== null && order.includes(active) ? active : (order[0] ?? null);

  const pick = (kind: string, item: { id: string; label: string }): void => {
    const before = text.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at === -1) return;
    const inserted = `@${item.label} `;
    const next = `${text.slice(0, at)}${inserted}${text.slice(caret)}`;
    setText(next);
    setPicked((current) => [
      ...current.filter((one) => !(one.kind === kind && one.id === item.id)),
      { kind: kind as AgentMentionKind, id: item.id, label: item.label },
    ]);
    setMore(null);
    setActive(null);
    const position = at + inserted.length;
    setCaret(position);
    window.requestAnimationFrame(() => {
      field.current?.setSelectionRange(position, position);
      field.current?.focus();
    });
  };

  const submit = (): void => {
    const trimmed = text.trim();
    if (trimmed === '' || busy) return;
    onSend(wireText(trimmed, picked));
    setText('');
    setPicked([]);
    setCaret(0);
  };

  const insertAt = (): void => {
    const node = field.current;
    const position = node?.selectionStart ?? text.length;
    const needsSpace = position > 0 && !/\s/u.test(text[position - 1] ?? ' ');
    const insert = needsSpace ? ' @' : '@';
    const next = `${text.slice(0, position)}${insert}${text.slice(position)}`;
    setText(next);
    setDismissed(false);
    const after = position + insert.length;
    setCaret(after);
    window.requestAnimationFrame(() => {
      node?.setSelectionRange(after, after);
      node?.focus();
    });
  };

  return (
    <div className="relative">
      {picking ? (
        <MentionPicker
          className="absolute bottom-[calc(100%+6px)] left-0 z-50 w-full max-w-[420px]"
          label={t('dashChat.mentions')}
          groups={groups}
          activeId={highlighted}
          onPick={pick}
          onMore={(kind) => {
            setMore(kind as AgentMentionKind);
          }}
          moreLabel={(count) => t('dashChat.more', { count })}
          emptyLabel={list === null ? t('dashChat.mentionsLoading') : t('dashChat.mentionsEmpty')}
        />
      ) : null}
      <div
        className={cn(
          'bg-card flex items-end gap-1 rounded-xl border p-1.5 pl-3 transition-shadow focus-within:ring-2 focus-within:ring-[var(--ring)]/30',
          floating && 'shadow-pop',
        )}
      >
        <Textarea
          ref={field}
          rows={1}
          value={text}
          placeholder={placeholder}
          aria-label={placeholder}
          autoFocus={autoFocus}
          className="max-h-32 min-h-8 flex-1 resize-none border-0 bg-transparent px-0 py-1.5 text-[13.5px] shadow-none focus-visible:ring-0"
          onFocus={onFocus}
          onChange={(event) => {
            setText(event.target.value);
            setCaret(event.target.selectionStart);
            setDismissed(false);
          }}
          onSelect={(event) => {
            setCaret(event.currentTarget.selectionStart);
          }}
          onBlur={() => {
            setDismissed(true);
          }}
          onKeyDown={(event) => {
            if (picking && order.length > 0) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const at = highlighted === null ? -1 : order.indexOf(highlighted);
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setActive(order[(at + step + order.length) % order.length] ?? null);
                return;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                const [kind = '', ...rest] = (highlighted ?? '').split(':');
                const id = rest.join(':');
                const item = groups
                  .find((group) => group.key === kind)
                  ?.items.find((one) => one.id === id);
                if (item !== undefined) pick(kind, item);
                return;
              }
            }
            if (picking && event.key === 'Escape') {
              event.preventDefault();
              setDismissed(true);
              return;
            }
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
        />
        {leading}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('dashChat.mention')}
          title={t('dashChat.mention')}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={insertAt}
        >
          <Icon name="at-sign" size={15} />
        </Button>
        <Button
          size="icon-sm"
          className="rounded-full"
          aria-label={t('dashChat.send')}
          title={t('dashChat.send')}
          disabled={busy || text.trim() === ''}
          onClick={submit}
        >
          <Icon name="arrow-right" size={15} className="-rotate-90" />
        </Button>
      </div>
    </div>
  );
}

function kindLabel(t: ReturnType<typeof useT>, kind: AgentMentionKind): string {
  switch (kind) {
    case 'contract':
      return t('dashChat.kind.contract');
    case 'lineItem':
      return t('dashChat.kind.lineItem');
    case 'period':
      return t('dashChat.kind.period');
    case 'person':
      return t('dashChat.kind.person');
    case 'task':
      return t('dashChat.kind.task');
    case 'invoice':
      return t('dashChat.kind.invoice');
    case 'externalCost':
      return t('dashChat.kind.externalCost');
  }
}
